import React, { useRef, useState } from 'react';
import { EntityCard, EntityCardSize, Stack, Box } from '@/components';
import { ReportSearchModel, ReportCardType, UiConfigurationModel } from '@/types';
import Flicking, { ChangedEvent, NeedPanelEvent } from '@egjs/react-flicking';
import { DIRECTION } from '@egjs/flicking';
import { carouselMotion, carouselItemMotion } from '@/animations';
import ReportCarouselHeading, { ReportHeadingModel } from './ReportCarouselHeading';

interface ReportSingleRowDragCarouselModel {
  cards: ReportSearchModel[];
  cardType: ReportCardType;
  heading?: ReportHeadingModel;
  uiConfiguration?: UiConfigurationModel;
  onNeedPanel(event: NeedPanelEvent): void;
  onCardClick?: (e: React.MouseEvent, data: ReportSearchModel) => void;
}

const ReportSingleRowDragCarousel = (data: ReportSingleRowDragCarouselModel) => {
  const { cards, cardType, uiConfiguration, onNeedPanel, onCardClick } = data;
  const [initialAnimationComplete, setInitialAnimationComplete] = useState<boolean>(false);
  const [nextDisabled, setNextDisabled] = useState<boolean>(false);
  const [prevDisabled, setPrevDisabled] = useState<boolean>(true);

  const carousel = useRef<Flicking>(null);

  const onCarouselChanged = ({ currentTarget, direction }: ChangedEvent) => {
    if (!currentTarget.camera.atEdge) {
      setPrevDisabled(false);
      setNextDisabled(false);
    } else {
      if (direction === DIRECTION.NEXT) {
        setPrevDisabled(false);
        setNextDisabled(true);
      } else {
        setPrevDisabled(true);
        setNextDisabled(false);
      }
    }
  };

  return (
    <Stack
      animation={initialAnimationComplete ? { ...carouselMotion, animate: 'active' } : carouselMotion}
      onAnimationComplete={() => setInitialAnimationComplete(true)}
    >
      <div className="container">
        <ReportCarouselHeading
          heading={data.heading}
          nextDisabled={nextDisabled}
          prevDisabled={prevDisabled}
          hideArrows={Boolean(uiConfiguration?.hideArrows)}
          hideViewMore={Boolean(uiConfiguration?.hideViewMore)}
          onNextClick={() => carousel.current?.next().catch(() => void 0)}
          onPrevClick={() => carousel.current?.prev().catch(() => void 0)}
        />
      </div>

      <Flicking
        ref={carousel}
        preventClickOnDrag
        preventDefaultOnDrag
        align="prev"
        horizontal
        bound
        onNeedPanel={onNeedPanel}
        className="xxl:pl-[240px] !overflow-visible pl-3 sm:pl-4 xl:pl-[136px]"
        onChanged={onCarouselChanged}
      >
        {cards.map((card, index) => (
          <div key={index} className="mx-[3px] flex sm:mx-[12px] sm:block">
            <Box animation={carouselItemMotion} className="">
              <EntityCard data={card} cardType={cardType} size={EntityCardSize.SMALL} onClick={onCardClick} />
            </Box>
          </div>
        ))}
      </Flicking>
    </Stack>
  );
};

export default ReportSingleRowDragCarousel;
